import { Injectable } from '@angular/core';
import { AngularGridInstance, CurrentFilter, CurrentPagination } from 'angular-slickgrid';
import {
  ColumnFilter,
  ColumnSort,
  ColumnVisibility,
  GridSnapshot,
} from '../layout-list/interface/IGridSnapshot';

interface FilterServiceEntry {
  searchTerms?: any[];
  operator?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GridStateApplierService {
  captureSnapshot(
    grid: AngularGridInstance,
    options?: { includeFilters?: boolean; includeSorting?: boolean; includeVisibility?: boolean }
  ): GridSnapshot {
    const includeFilters = options?.includeFilters !== false;
    const includeSorting = options?.includeSorting !== false;
    const includeVisibility = options?.includeVisibility !== false;

    return {
      columnVisibility: includeVisibility ? this.getColumnVisibility(grid) : [],
      filters: includeFilters ? this.getColumnFilters(grid) : [],
      sorting: includeSorting ? this.getColumnSorting(grid) : [],
    };
  }

  applySnapshot(
    grid: AngularGridInstance,
    snapshot: GridSnapshot,
    options?: { triggerQuery?: boolean; pagination?: CurrentPagination | null }
  ): void {
    const slickGrid = grid?.slickGrid;
    const originalOptions = slickGrid?.getOptions?.() || {};
    const shouldTemporarilyShowHeaderRow = originalOptions.showHeaderRow === false;

    if (slickGrid?.setOptions) {
      if (!originalOptions.enableFiltering || shouldTemporarilyShowHeaderRow) {
        slickGrid.setOptions(
          { ...originalOptions, enableFiltering: true, showHeaderRow: true },
          false,
          true
        );
        slickGrid.setHeaderRowVisibility?.(true);
        slickGrid.setColumns?.(slickGrid.getColumns?.() || []);
        slickGrid.invalidate?.();
        slickGrid.render?.();
      }
    }

    this.applyColumnVisibility(snapshot.columnVisibility, grid);
    this.applyColumnSorting(snapshot.sorting, grid);
    this.applyColumnFilters(snapshot.filters, grid);

    if (shouldTemporarilyShowHeaderRow && slickGrid?.setOptions) {
      slickGrid.setOptions(
        { ...originalOptions, enableFiltering: true, showHeaderRow: false },
        false,
        true
      );
      slickGrid.setHeaderRowVisibility?.(false);
      slickGrid.invalidate?.();
      slickGrid.render?.();
    }

    this.autosizeColumns(slickGrid);

    if (options?.pagination) {
      this.applyPagination(grid, options.pagination);
    }

    if (options?.triggerQuery !== false) {
      this.triggerBackendQuery(grid);
    }
  }

  applyFilters(
    grid: AngularGridInstance,
    filters: ColumnFilter[],
    options?: { triggerQuery?: boolean }
  ): void {
    this.applyColumnFilters(filters, grid);
    if (options?.triggerQuery !== false) {
      this.triggerBackendQuery(grid);
    }
  }

  applySorting(grid: AngularGridInstance, sorting: ColumnSort[], options?: { triggerQuery?: boolean }): void {
    this.applyColumnSorting(sorting, grid);
    if (options?.triggerQuery !== false) {
      this.triggerBackendQuery(grid);
    }
  }

  applyVisibility(grid: AngularGridInstance, visibility: ColumnVisibility[]): void {
    this.applyColumnVisibility(visibility, grid);
  }

  applyPagination(grid: AngularGridInstance, pagination: CurrentPagination): void {
    const backendService = grid.backendService;
    if (backendService?.updatePagination) {
      backendService.updatePagination(pagination.pageNumber, pagination.pageSize);
    }

    if (grid.paginationService) {
      grid.paginationService.goToPageNumber(pagination.pageNumber, undefined, false);
      grid.paginationService.refreshPagination(false, false, false);
    }
  }

  private triggerBackendQuery(grid: AngularGridInstance): void {
    grid?.extensionService?.refreshBackendDataset();
  }

  private getColumnVisibility(grid: AngularGridInstance): ColumnVisibility[] {
    const allColumns = grid?.gridService?.getAllColumnDefinitions() || [];
    const visibleColumns = grid?.slickGrid?.getColumns?.() || [];
    const visibleIds = new Set(visibleColumns.map(col => col.id as string));

    return allColumns
      .filter(col => !this.isSystemColumn(col.id as string))
      .map(col => ({
        columnId: col.id as string,
        visible: visibleIds.has(col.id as string),
      }));
  }

  private getColumnFilters(grid: AngularGridInstance): ColumnFilter[] {
    const columnFilters: Record<string, FilterServiceEntry> = grid?.filterService?.getColumnFilters() || {};
    const fromFilterService = Object.entries(columnFilters).map(([columnId, filter]) => ({
      columnId,
      searchTerms: filter?.searchTerms,
      operator: filter?.operator,
    }));

    if (fromFilterService.length > 0) {
      return fromFilterService;
    }

    const backendFilters = grid?.backendService?.getCurrentFilters?.();
    if (Array.isArray(backendFilters)) {
      return backendFilters.map(filter => ({
        columnId: filter.columnId,
        searchTerms: filter.searchTerms,
        operator: filter.operator,
      }));
    }

    if (backendFilters && typeof backendFilters === 'object') {
      const filterRecord: Record<string, unknown> = backendFilters;
      return Object.entries(filterRecord).map(([columnId, filter]) => {
        const terms = filter !== null && typeof filter === 'object' && 'searchTerms' in filter
          ? filter.searchTerms : undefined;
        const operator = filter !== null && typeof filter === 'object' && 'operator' in filter
          ? filter.operator : undefined;
        let searchTerms: unknown[];
        if (Array.isArray(terms)) {
          searchTerms = terms;
        } else if (Array.isArray(filter)) {
          searchTerms = filter;
        } else {
          searchTerms = [filter];
        }
        return {
          columnId,
          searchTerms,
          operator: typeof operator === 'string' ? operator : undefined,
        };
      });
    }

    return [];
  }

  private getColumnSorting(grid: AngularGridInstance): ColumnSort[] {
    const sortService = grid?.sortService;
    const currentSorting = sortService?.getCurrentColumnSorts() || [];

    return currentSorting.map(
      (sort, index) =>
        ({
          columnId: sort.columnId,
          sortCol: sort.sortCol?.field || sort.columnId,
          sortAsc: sort.sortAsc !== undefined ? sort.sortAsc : null,
          sortOrder: index + 1,
        } as ColumnSort)
    );
  }

  private applyColumnVisibility(visibility: ColumnVisibility[], grid: AngularGridInstance): void {
    const allColumns = grid?.gridService?.getAllColumnDefinitions() || [];
    const currentVisible = new Set(
      (grid?.slickGrid?.getColumns?.() || []).map(col => col.id as string)
    );
    const visibilityMap = new Map(
      visibility.filter(v => !this.isSystemColumn(v.columnId)).map(v => [v.columnId, v.visible])
    );

    const visibleColumns = allColumns.filter(col => {
      const colId = col.id as string;
      if (this.isSystemColumn(colId)) {
        return true;
      }
      if (visibilityMap.has(colId)) {
        return visibilityMap.get(colId) === true;
      }
      return currentVisible.has(colId);
    });

    if (visibleColumns.length > 0) {
      grid.slickGrid?.setColumns(visibleColumns);
    }
  }

  private applyColumnFilters(filters: ColumnFilter[], grid: AngularGridInstance): void {
    const backendService = grid.backendService;
    backendService?.clearFilters?.();
    backendService?.updateFilters?.(filters as any, true);

    const currentFilters = backendService?.getCurrentFilters?.() as CurrentFilter[] | undefined;
    if (currentFilters) {
      const slickGrid = grid?.slickGrid;
      const options = slickGrid?.getOptions?.() || {};
      const hasFilterableColumns = (slickGrid?.getColumns?.() || []).some(
        (col: any) => col?.filterable || col?.filter
      );
      const filtersMetadataLength = grid?.filterService?.getFiltersMetadata?.()?.length || 0;

      if (options.enableFiltering && hasFilterableColumns && filtersMetadataLength > 0) {
        grid?.filterService?.updateFilters(currentFilters, false, false);
      }
    }
  }

  private applyColumnSorting(sorting: ColumnSort[], grid: AngularGridInstance): void {
    const backendService = grid.backendService;
    backendService?.clearSorters?.();

    const sortedByOrder = [...sorting].sort(
      (a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity)
    );

    const backendSorters = sortedByOrder.map(sorter => ({
      columnId: sorter.columnId,
      direction: sorter.sortAsc ? 'ASC' : 'DESC',
    }));

    backendService?.updateSorters?.(backendSorters as any);

    const sorters = sortedByOrder.map((sorter, index) => ({
      columnId: sorter.columnId,
      sortCol: {
        id: sorter.columnId,
        field: sorter.sortCol,
      },
      sortAsc: sorter.sortAsc,
      sortOrder: index,
    }));

    grid.slickGrid?.setSortColumns(sorters as any);
  }

  private autosizeColumns(slickGrid: any): void {
    if (!slickGrid?.setOptions || !slickGrid?.autosizeColumns) {
      return;
    }

    requestAnimationFrame(() => {
      slickGrid.setOptions({ enableAutoSizeColumns: true });
      slickGrid.autosizeColumns();
      slickGrid.setOptions({ enableAutoSizeColumns: false });
    });
  }

  private isSystemColumn(columnId?: string): boolean {
    return typeof columnId === 'string' && columnId.startsWith('_');
  }
}
